import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import api from "../lib/api";
import MenuCard from "../components/MenuCard";
import ItemDetailSheet from "../components/ItemDetailSheet";
import { useSettings } from "../context/SettingsContext";

export default function Menu() {
  const [menu, setMenu] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const { settings } = useSettings();

  useEffect(() => {
    api.get("/menu").then(({ data }) => {
      setMenu(data);
      if (data.length) setActiveCat(data[0].id);
    });
  }, []);

  const extras = menu.find((c) => c.slug === "extras")?.items || [];

  const filtered = useMemo(() => {
    return menu
      .map((c) => ({
        ...c,
        items: (c.items || []).filter((i) => i.name.toLowerCase().includes(q.toLowerCase())),
      }))
      .filter((c) => c.items.length > 0);
  }, [menu, q]);

  if (settings && settings.accepting_orders === false) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl text-sapore-purple mb-4">We're currently closed</h1>
        <p className="text-sapore-inkSoft" data-testid="closed-message">{settings.closed_message}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-sapore-purple">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-16">
          <p className="text-sapore-gold uppercase tracking-[0.3em] text-xs mb-3">The Menu</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-sapore-cream">Breakfast By Sapore</h1>
          <p className="text-sapore-cream/80 mt-3 max-w-lg">Fresh breakfast, sides, and cold-pressed juices — delivered.</p>
        </div>
      </div>

      <div className="sticky top-20 z-30 bg-sapore-cream/95 backdrop-blur-md border-b border-sapore-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="flex gap-2 overflow-x-auto hide-scroll flex-1">
            {menu.map((c) => (
              <button
                key={c.id}
                data-testid={`category-pill-${c.slug}`}
                onClick={() => {
                  setActiveCat(c.id);
                  document.getElementById(`cat-${c.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                  activeCat === c.id ? "bg-sapore-purple text-sapore-cream border-sapore-purple" : "bg-white text-sapore-ink border-sapore-border hover:border-sapore-purple"
                }`}
              >
                {c.display_name}
              </button>
            ))}
          </div>
          <div className="relative sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sapore-muted" />
            <input
              data-testid="menu-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the menu"
              className="w-full pl-9 pr-4 py-2 rounded-full border border-sapore-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sapore-purple/30"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 space-y-14">
        {filtered.map((c) => (
          <section key={c.id} id={`cat-${c.id}`} className="scroll-mt-40">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-sapore-purple mb-6">{c.display_name}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {c.items.map((item, i) => (
                <MenuCard key={item.id} item={item} index={i} onOpen={setActive} />
              ))}
            </div>
          </section>
        ))}
        {filtered.length === 0 && <p className="text-center text-sapore-muted py-20">No items match your search.</p>}
      </div>

      <ItemDetailSheet item={active} extras={extras} open={!!active} onClose={() => setActive(null)} />
    </div>
  );
}
